'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
export interface TopMarketsByPrizeRowProps {
  marketPubkey: string;
  marketTitle: string;
  /** Live D6 prize pool string, supplied by the parent's live cache read. */
  prizePool: string;
  /** 1-based leaderboard rank. */
  rank: number;
  /** Whether this row is the currently spotlighted (expanded) pool. */
  isActive: boolean;
  /** Hairline above the row — hidden on the active row and the one after it. */
  showSeparator: boolean;
}

/**
 * A single Biggest Prize Pools leaderboard row. Renders two cross-faded
 * layers — a compact one-liner (inactive) and an expanded image-backed hero
 * (active). The parent owns the cover image, the flex-grow expand, and the
 * spotlight cadence; this component is presentational.
 */
export function TopMarketsByPrizeRow(t0) {
  const $ = _c(44);
  const {
    marketPubkey,
    marketTitle,
    prizePool,
    rank,
    isActive,
    showSeparator
  } = t0;
  const t = useTranslations("markets.top_markets_by_prize");
  const {
    formatD6
  } = useFormattedCurrency();
  const href = `/markets/${marketPubkey}`;
  const intent = useIntentRoutePrefetch(href);
  let t1;
  if ($[0] !== formatD6) {
    t1 = value => formatD6(String(Math.round(value)), "compactDecimal");
    $[0] = formatD6;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formatPrize = t1;
  let t2;
  if ($[2] !== marketTitle || $[3] !== t) {
    t2 = t("aria_row_label", {
      marketTitle
    });
    $[2] = marketTitle;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = isActive ? "true" : "false";
  const t4 = showSeparator && "before:absolute before:inset-x-3.5 before:top-0 before:z-10 before:h-px before:bg-foreground-primary/7";
  let t5;
  if ($[5] !== t4) {
    t5 = cn("group/prow relative flex h-full min-h-0 flex-1 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = isActive ? "opacity-0" : "opacity-70";
  let t7;
  if ($[7] !== t6) {
    t7 = cn("absolute inset-0 flex items-center gap-2.5 px-3.5 transition-opacity duration-[350ms] ease-default motion-reduce:transition-none", t6);
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== rank) {
    t8 = <span className="w-3.5 shrink-0 text-center text-caption font-semibold tabular-nums text-foreground-tertiary">{rank}</span>;
    $[9] = rank;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== marketTitle) {
    t9 = <span className="min-w-0 flex-1 truncate text-body font-normal text-foreground-secondary group-hover/prow:underline group-hover/prow:underline-offset-2">{marketTitle}</span>;
    $[11] = marketTitle;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = Number(prizePool);
  let t11;
  if ($[13] !== formatPrize || $[14] !== t10) {
    t11 = <span className="shrink-0 text-body font-semibold tabular-nums text-foreground-primary"><AnimatedNumber value={t10} format={formatPrize} /></span>;
    $[13] = formatPrize;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== isActive || $[17] !== t11 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t12 = <div aria-hidden={isActive} className={t7}>{t8}{t9}{t11}</div>;
    $[16] = isActive;
    $[17] = t11;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = !isActive;
  const t14 = isActive ? "opacity-100" : "opacity-0";
  let t15;
  if ($[22] !== t14) {
    t15 = cn("absolute inset-0 flex flex-col justify-center gap-0 px-[13px] transition-opacity duration-[350ms] ease-default motion-reduce:transition-none", t14);
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const t16 = Number(prizePool);
  let t17;
  if ($[24] !== formatPrize || $[25] !== t16) {
    t17 = <p data-testid="top-markets-by-prize-row-prize" className="text-display-sm font-semibold tabular-nums text-foreground-primary"><AnimatedNumber value={t16} format={formatPrize} /></p>;
    $[24] = formatPrize;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== marketTitle) {
    t18 = <p data-testid="top-markets-by-prize-row-market-title" className="line-clamp-2 max-w-full text-body font-normal leading-snug text-foreground-secondary group-hover/prow:underline group-hover/prow:underline-offset-2 xl:max-w-[64%]">{marketTitle}</p>;
    $[27] = marketTitle;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t13 || $[30] !== t15 || $[31] !== t17 || $[32] !== t18) {
    t19 = <div aria-hidden={t13} className={t15}>{t17}{t18}</div>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== href || $[35] !== intent || $[36] !== marketPubkey || $[37] !== rank || $[38] !== t12 || $[39] !== t19 || $[40] !== t2 || $[41] !== t3 || $[42] !== t5) {
    t20 = <Link href={href} prefetch={false} {...intent} aria-label={t2} data-testid="top-markets-by-prize-row" data-pubkey={marketPubkey} data-rank={rank} data-active={t3} className={t5}>{t12}{t19}</Link>;
    $[34] = href;
    $[35] = intent;
    $[36] = marketPubkey;
    $[37] = rank;
    $[38] = t12;
    $[39] = t19;
    $[40] = t2;
    $[41] = t3;
    $[42] = t5;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
}